import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DrQuackMascot, { DuckTypingDots } from './DrQuackMascot';
import './AIIntroScreen.css';

/**
 * Welcome lines the duck "types" out. Kept short on purpose — this is a
 * one-time greet, not a tutorial. The actual conversation happens in the
 * floating chat widget.
 */
const DUCK_LINES = [
  "Hi! I'll help you understand your HRV data.",
  'Ask me anything, anytime.',
];

/**
 * Full-screen intro shown once after onboarding completes.
 *
 * Flow:
 *   RegisterPage Step 3 (PUT /user/{id}) → /ai-intro (THIS) → /dashboard
 *
 * Once the user has seen this screen we set localStorage.aiIntroSeen so a
 * stray /ai-intro visit later just bounces them to /dashboard. The mobile
 * app uses router.replace for the same effect; localStorage is the web
 * equivalent that survives a hard refresh.
 */
export default function AIIntroScreen() {
  const navigate = useNavigate();

  // Lines that have finished typing (each becomes its own bubble)
  const [visibleLines, setVisibleLines] = useState([]);
  // Line currently mid-type — characters revealed one at a time
  const [typingLine, setTypingLine] = useState('');
  const [typingIndex, setTypingIndex] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Refs to keep the typing timers controllable so Skip can't double-commit
  const typingIntervalRef = useRef(null);
  const commitTimeoutRef = useRef(null);
  const skippedRef = useRef(false);

  // tiny mount delay so the entrance animation has something to animate from
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 16);
    return () => clearTimeout(t);
  }, []);

  // Typewriter loop. Same timing as the mobile screen (~35ms per character,
  // 420ms pause between lines).
  useEffect(() => {
    if (typingIndex >= DUCK_LINES.length) return;

    skippedRef.current = false;
    const target = DUCK_LINES[typingIndex];
    let charIndex = 0;
    setTypingLine('');

    typingIntervalRef.current = setInterval(() => {
      charIndex += 1;
      setTypingLine(target.slice(0, charIndex));

      if (charIndex >= target.length) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;

        commitTimeoutRef.current = setTimeout(() => {
          if (skippedRef.current) return;
          setVisibleLines((prev) => [...prev, target]);
          setTypingLine('');
          setTypingIndex((i) => i + 1);
        }, 420);
      }
    }, 35);

    return () => {
      if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
      if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);
    };
  }, [typingIndex]);

  const allDone = typingIndex >= DUCK_LINES.length;

  const finishAndContinue = () => {
    navigate('/dashboard', { replace: true });
  };

  const handleSkip = () => {
    skippedRef.current = true;
    if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
    if (commitTimeoutRef.current) clearTimeout(commitTimeoutRef.current);

    if (!allDone) {
      // Reveal everything immediately so the user can read it before they
      // hit Continue, just like the mobile flow.
      setVisibleLines([...DUCK_LINES]);
      setTypingLine('');
      setTypingIndex(DUCK_LINES.length);
    } else {
      finishAndContinue();
    }
  };

  return (
    <div className={`ai-intro-page ${mounted ? 'is-mounted' : ''}`}>
      <div className="ai-intro-bg-glow ai-intro-bg-glow--top" />
      <div className="ai-intro-bg-glow ai-intro-bg-glow--bottom" />

      <div className="ai-intro-inner">
        {/* Five filled dots — matches the mobile screen's progress row.
            Cosmetic, not functional. */}
        <div className="ai-intro-progress-row">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="ai-intro-progress-dot" />
          ))}
        </div>

        <h1 className="ai-intro-title">Meet your AI companion</h1>
        <p className="ai-intro-subtitle">
          Built in, always on — ready to explain anything.
        </p>

        <div className="ai-intro-stage">
          <div className="ai-intro-halo" aria-hidden="true" />

          <div className="ai-intro-stage-row">
            <div className="ai-intro-bubbles-col">
              {visibleLines.map((line, i) => (
                <ChatBubble key={i} text={line} />
              ))}

              {typingLine !== '' && <ChatBubble text={typingLine} caret />}

              {visibleLines.length === 0 && typingLine === '' && (
                <div className="ai-intro-bubble">
                  <DuckTypingDots color="#5CB89A" size={7} />
                </div>
              )}
            </div>

            <div className="ai-intro-duck-col">
              <DrQuackMascot size={320} animate />
            </div>
          </div>
        </div>

        <div className="ai-intro-bottom-bar">
          {!allDone ? (
            <button
              type="button"
              className="ai-intro-skip-btn"
              onClick={handleSkip}
            >
              Skip intro
            </button>
          ) : (
            <div style={{ height: 44 }} />
          )}

          <button
            type="button"
            className={`ai-intro-continue-btn ${!allDone ? 'is-dim' : ''}`}
            onClick={finishAndContinue}
          >
            {allDone ? 'Continue' : 'Continue anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ text, caret }) {
  return (
    <div className="ai-intro-bubble ai-intro-bubble--enter">
      <span className="ai-intro-bubble-text">
        {text}
        {caret && <span className="ai-intro-bubble-caret">▎</span>}
      </span>
      <span className="ai-intro-bubble-tail" />
    </div>
  );
}